import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "./utils/cn";

export type IconButtonVariant = "ghost" | "soft" | "solid";
export type IconButtonSize = "sm" | "md" | "lg";

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: ReactNode;
  /** `ghost` = tinted/transparent, `solid` = filled brand. */
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /** Required for accessibility — icon-only buttons need a label. */
  "aria-label": string;
}

const base =
  "inline-flex shrink-0 items-center justify-center rounded-pill outline-none " +
  "transition-[transform,background-color] duration-150 ease-out-soft active:scale-90 " +
  "focus-visible:ring focus-visible:ring-offset-0 " +
  "disabled:pointer-events-none disabled:opacity-40";

const variantClasses: Record<IconButtonVariant, string> = {
  // transparent by default (no permanent chip); subtle surface tint on hover —
  // right for inline/table-row/close actions
  ghost: "bg-transparent text-secondary hover:bg-surface-subtle",
  // tinted chip (the old RoundIconButton look) — for emphasis e.g. modal close on mobile
  soft: "bg-surface-subtle text-secondary hover:brightness-105",
  solid: "bg-secondary text-white hover:brightness-105",
};

const sizeClasses: Record<IconButtonSize, string> = {
  sm: "h-6 w-6 text-[14px]",
  md: "h-8 w-8 text-[16px]",
  lg: "h-10 w-10 text-[18px]",
};

/**
 * Icon-only button. Renders a real <button> (unlike common/RoundIconButton,
 * which was a div[role=button]). Supersedes RoundIconButton / CloseButton.
 */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      variant = "ghost",
      size = "md",
      className,
      disabled,
      type = "button",
      ...rest
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        className={cn(
          base,
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        {...rest}
      >
        {icon}
      </button>
    );
  }
);

IconButton.displayName = "IconButton";

export default IconButton;
